import { Dispatch, SetStateAction, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Button, CopyToClipboard, Icon } from '@intellecteu/common-ui';
import { userManageRightsPath } from 'app/constants';
import { API_VERSION_V1, UserDto } from 'data';
import { ui } from 'ui/styles';

type ColumnProps = {
  participantName: string;
  isRemote?: boolean;
  setUserToRemove: Dispatch<SetStateAction<string | null>>;
  apiVersion: string;
  /** When set, participant has a validator parent (no Local Parties tab, no filter on Parties). */
  validatorParent?: string | null;
};

export const useUsersTableColumns = ({
  participantName,
  isRemote,
  setUserToRemove,
  apiVersion,
  validatorParent,
}: ColumnProps) => {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<UserDto>[]>(
    () => [
      {
        header: 'Name',
        cell: (row) => row.renderValue(),
        accessorKey: apiVersion === API_VERSION_V1 ? 'userId' : 'id',
        id: apiVersion === API_VERSION_V1 ? 'name' : 'id',
        sortingFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 250,
      },
      {
        header: 'Party ID',
        cell: ({ getValue }) =>
          getValue() ? (
            <CopyToClipboard value={getValue() as string} iconSize="s" textMaxWidth={400} withTruncation />
          ) : (
            '–'
          ),
        accessorKey: 'primaryParty',
        enableSorting: false,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        size: 150,
        cell: ({ row: { original } }) => {
          const userName = (apiVersion === API_VERSION_V1 ? original.userId : original.id) as string;
          return (
            <div className={ui.cellActions}>
              <Button
                variant="tertiary"
                size="xs"
                onClick={() =>
                  navigate(userManageRightsPath(participantName, userName, isRemote), {
                    state: {
                      apiVersion,
                      isStandalone: !validatorParent,
                    },
                  })
                }
              >
                <Icon icon={Icon.icons.edit} size="m" />
                Manage rights
              </Button>

              <Button variant="tertiary" size="xs" onClick={() => setUserToRemove(userName)}>
                <Icon icon={Icon.icons.remove} size="m" />
                Remove
              </Button>
            </div>
          );
        },
      },
    ],
    [participantName, isRemote, apiVersion, validatorParent, setUserToRemove, navigate],
  );

  return columns;
};
